import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Switch, Route, Redirect, withRouter } from "react-router-dom";
import { MuiThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { SnackbarProvider } from "notistack";
import { fetchAppSettings, clientSwitchMaintenanceMode, checkVersionForMaintenance, fetchUsers, fetchUser,
  setIsMobile, setDisplayMode } from "store/actions";
import { Underwrite, SaveUWDialog } from "components/underwrite/acquisition";
import {
  AppNotifier,
  Stack,
  OverflowContainer,
  ConfirmationModal,
  muiTheme,
  TitleBar,
  VersionRefreshModal,
  KeepMounted,
  ErrorMessageListModal,
} from "components/layout";
import { ChannelWorkflow } from "components/underwrite/acquisition/channelWorkflow";
import SearchDrawer from "components/search/SearchDrawer";
import NavigationLock from "./layout/NavigationLock";
import { Constants, getModuleByLocation } from "utilities/constants";
import { Explore } from "components/explore";
import { Dashboard, IdSearch } from "components/dashboard";
import { PostAcq } from "components/underwrite/postAcquisition";
import { PostAcqWorkflow } from "components/underwrite/postAcquisition/postAcqWorkflow";
import { Assumptions } from "components/assumptions";
import { Administration } from "components/admin";
import { ProspectorRoutes, Prospects } from "components/explore/prospector";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { Error as ErrorIcon, Info as InfoIcon, Warning as WarningIcon, CheckCircle as SuccessIcon } from "@material-ui/icons";
import Maintenance from "components/admin/Maintenance";
import { tabLimitEnforcer } from "components/tabLimitEnforcer";
import ErrorModal from "components/tabLimitEnforcer/ErrorModal";
import { AuthorizedRoute, RouteWithTitle } from "routes";
import { ErrorBoundary, AppFallback } from "components/hoc";
import SaveUWDialogProvider from "components/underwrite/shared/SaveUWDialog";
import _ from "lodash";

const notistackIcons = {
  success: <SuccessIcon style={{ fontSize: 20, marginRight: 8 }} />,
  error: <ErrorIcon style={{ fontSize: 20, marginRight: 8 }} />,
  warning: <WarningIcon style={{ fontSize: 20, marginRight: 8 }} />,
  info: <InfoIcon style={{ fontSize: 20, marginRight: 8 }} />,
};

var windowWidth = null;

const App = ({
  location,
  user,
  users,
  fetchAppSettings,
  appSettings,
  maintenanceMode,
  clientSwitchMaintenanceMode,
  currentAppVersion,
  checkVersionForMaintenance,
  fetchUsers,
  fetchUser,
  setIsMobile,
  setDisplayMode,
  hasSomeDirtyFlag,
  saveUWDialogParameters
}) => {
  const [prospectorModalOpen, setProspectorModalOpen] = useState(false);
  const [prospectsHasBeenMounted, setProspectsHasBeenMounted] = useState(false);
  const [refreshProspectsGrid, setRefreshProspectsGrid] = useState(0); // Increase to trigger a refresh on prospects grid NOTE: Can extract this to redux in the future
  useEffect(() => {
    fetchAppSettings();
    checkVersionForMaintenance();
  }, []);

  // Changes in the app version will cause  Maintenance popup to close
  useEffect(() => {
    if (currentAppVersion && currentAppVersion.length > 0) {
      console.log("Server is online. Code version: ", currentAppVersion);
      clientSwitchMaintenanceMode(false);
    } else {
      console.log("Server is offline");
    }
  }, [currentAppVersion]);

  const handleWindowResizeDebounced = _.debounce(handleWindowResize, 500);
  useEffect(() => {
    window.addEventListener("resize", handleWindowResizeDebounced);
    handleWindowResize();
    return () => {
      window.removeEventListener("resize", handleWindowResizeDebounced);
    }
  }, []);

  const getDisplayMode = (width) => {
    const mobileBreakpoint = 812;
    const laptopBreakpoint = 1536;
    const desktopBreakpoint = 2560;
    if (width >= desktopBreakpoint) {
      return Constants.DisplayMode.DESKTOP;
    } else if (width >= laptopBreakpoint) {
      return Constants.DisplayMode.LAPTOP;
    } else {
      return Constants.DisplayMode.MOBILE;
    }
  }
  
  function handleWindowResize(e) {
    const breakpoint = 812;
    const { innerWidth } = window;
    if (windowWidth) {
        const newDisplayMode = getDisplayMode(innerWidth);
        const prevDisplayMode = getDisplayMode(windowWidth);
        if (newDisplayMode !== prevDisplayMode) {
          setDisplayMode(newDisplayMode);
        }

        if (innerWidth > breakpoint && windowWidth <= breakpoint) {            
            setIsMobile(false);
        } else if (innerWidth < breakpoint && windowWidth >= breakpoint) {
          setIsMobile(true);
        }
    } else {
        setDisplayMode(getDisplayMode(innerWidth))

        if (innerWidth > breakpoint) {
          setIsMobile(false);
        } else if (innerWidth <= breakpoint) {
          setIsMobile(true);
        }
    }
    windowWidth = innerWidth;
  }
  const hasNoMinWidth = location.pathname === Constants.Routes.EXPLORE || location.pathname.startsWith(Constants.Routes.UNDERWRITE_PROPERTY);
  const isUnderwriteModule = [Constants.Module.UNDERWRITE, Constants.Module.POSTACQ].includes(getModuleByLocation(location));
  return (
    <MuiThemeProvider theme={muiTheme}>
      <ErrorBoundary fallbackRender={errorInfo => <AppFallback errorInfo={errorInfo} />}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <SaveUWDialogProvider>
            <React.Fragment>
              <SnackbarProvider
                maxSnack={4}
                autoHideDuration={5000}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                iconVariant={notistackIcons}
              >
                <AppNotifier />
                <ErrorMessageListModal />
              </SnackbarProvider>
              <CssBaseline />
              {maintenanceMode && <Maintenance />}
              {(appSettings.isLoading ? null : appSettings.hasData) && (
                <Stack
                  divStyle={{
                    height: "100%",
                    minWidth: prospectorModalOpen || hasNoMinWidth ? null : 1300, // Don't enforce minWidth on Explore or Prop UW
                  }}
                >
                  {!prospectorModalOpen && <TitleBar/>}
                  {isUnderwriteModule && <SearchDrawer />}
                  <NavigationLock
                    when={hasSomeDirtyFlag}
                    message={(newLocation, action) => {
                      console.log(newLocation, action);
                      return action === "POP" ? "You have unsaved changes, are you sure you want to leave?" : true;
                    }}
                  />
                  <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
                    <OverflowContainer style={{ position: "relative", display: "flex" }}>
                      {prospectorModalOpen && (
                        <AuthorizedRoute
                          path={Constants.Routes.EXPLORE_PROSPECTOR}
                          isAuthorized={user.roles.sourcingUser}
                          render={(props) => (
                            <ProspectorRoutes
                              {...props}
                              setProspectorModalOpen={setProspectorModalOpen}
                              prospectorModalOpen={prospectorModalOpen}
                              refreshProspectsGrid={refreshProspectsGrid}
                              setRefreshProspectsGrid={setRefreshProspectsGrid}
                            />
                          )}
                        />
                      )}
                      {user.roles.underwriteUser && (
                        <AuthorizedRoute
                          path={Constants.Routes.CHANNEL_WORKFLOW}
                          title={location.pathname === Constants.Routes.CHANNEL_WORKFLOW ? Constants.RouteTitles.CHANNEL_WORKFLOW : ''}
                          isAuthorized={user.roles.underwriteUser}
                          children={(props) => <ChannelWorkflow {...props} />}
                        />
                      )}
                      {user.roles.underwriteUser && (
                        <AuthorizedRoute
                          path={Constants.Routes.POSTACQ_WORKFLOW}
                          title={location.pathname === Constants.Routes.POSTACQ_WORKFLOW ? Constants.RouteTitles.POSTACQ_WORKFLOW : ''}
                          isAuthorized={user.roles.underwriteUser}
                          children={(props) => <PostAcqWorkflow {...props} />}
                        />
                      )}
                      {user.roles.sourcingUser && (
                        <AuthorizedRoute
                          path={Constants.Routes.EXPLORE_CONDUCTOR}
                          title={location.pathname === Constants.Routes.EXPLORE_CONDUCTOR ? Constants.RouteTitles.EXPLORE_CONDUCTOR : ''}
                          isAuthorized={user.roles.sourcingUser}
                          children={(props) => (
                            <KeepMounted
                              isMounted={location.pathname === Constants.Routes.EXPLORE_CONDUCTOR}
                              componentHasBeenMounted={prospectsHasBeenMounted}
                              setComponentHasBeenMounted={setProspectsHasBeenMounted}
                              render={() => (
                                <Prospects
                                  {...props}
                                  setProspectorModalOpen={setProspectorModalOpen}
                                  refreshProspectsGrid={refreshProspectsGrid}
                                  setRefreshProspectsGrid={setRefreshProspectsGrid}
                                />
                              )}
                            />
                          )}
                        />
                      )}
                      <Switch>
                        <Route
                          path={Constants.Routes.EXPLORE}
                          render={(props) => (
                            <Explore
                              {...props}
                              setProspectorModalOpen={setProspectorModalOpen}
                              prospectorModalOpen={prospectorModalOpen}
                              refreshProspectsGrid={refreshProspectsGrid}
                              setRefreshProspectsGrid={setRefreshProspectsGrid}
                            />
                          )}
                        />
                        <AuthorizedRoute path={Constants.Routes.UNDERWRITE} isAuthorized={user.roles.underwriteUser} component={Underwrite} />
                        <RouteWithTitle exact path={Constants.Routes.DASHBOARD_SEARCH} title={Constants.RouteTitles.DASHBOARD_SEARCH} component={IdSearch} />
                        <RouteWithTitle path={Constants.Routes.DASHBOARD} title={Constants.RouteTitles.DASHBOARD} component={Dashboard} />
                        <AuthorizedRoute path={Constants.Routes.POSTACQ} isAuthorized={user.roles.underwriteUser} component={PostAcq} />
                        <RouteWithTitle path={Constants.Routes.ASSUMPTIONS} title={Constants.RouteTitles.ASSUMPTIONS} component={Assumptions} />
                        {/* For debug of admin page */}
                        {/* <Route path={Constants.Routes.ADMINISTRATION} component={Administration} /> */}
                        <AuthorizedRoute path={Constants.Routes.ADMINISTRATION} title={Constants.RouteTitles.ADMINISTRATION} component={Administration} isAuthorized={user.roles.administrator} />
                        {/* This will handle both index and bad routes */}
                        <Route
                          render={({ location }) => {
                            if (!location.pathname.startsWith(Constants.Routes.CHANNEL_WORKFLOW)) {
                              // Avoid extra redirect if already going to channelWorkflow, since it renders above Switch
                              if (location.pathname !== "/") {
                                // Warn for bad routes
                                console.log(location.pathname, "Bad route specified, redirecting...");
                              }
                              // const redirectRoute = user.roles.underwriteUser ? Constants.Routes.CHANNEL_WORKFLOW : Constants.Routes.EXPLORE;
                              // return <Redirect to={redirectRoute} />;
                              let redirectRoute;
                              if (user.roles.underwriteUser) {
                                redirectRoute = Constants.Routes.POSTACQ_WORKFLOW;
                              } else if (user.roles.sourcingManager || user.roles.sourcingUser) {
                                redirectRoute = Constants.Routes.EXPLORE_CONDUCTOR;
                              } else if (user.roles.exploreUser) {
                                redirectRoute = Constants.Routes.EXPLORE;
                              } else {
                                redirectRoute = Constants.Routes.DASHBOARD;
                              }
                              return <Redirect to={redirectRoute} />;
                            }
                            return null;
                          }}
                        />
                      </Switch>
                      <ConfirmationModal />
                      <VersionRefreshModal />
                    </OverflowContainer>
                  </div>
                </Stack>
              )}
            </React.Fragment>
          </SaveUWDialogProvider>
        </MuiPickersUtilsProvider>
      </ErrorBoundary>
    </MuiThemeProvider>
  );
};

const mapState = (state) => {
  const hasSomeDirtyFlag = state.propertyUWState.valuationIsDirty || state.exploreViews.selectedViewIsDirty || state.postAcqUWState.valuationIsDirty;
  return {
    hasSomeDirtyFlag,
    appSettings: state.appState.appSettings,
    user: state.appState.user,
    users: state.appState.users,
    maintenanceMode: state.adminState.maintenanceMode,
    currentAppVersion: state.adminState.currentAppVersion,
    saveUWDialogParameters: state.propertyUWState.saveUWDialogParameters,
  }
};

const mapDispatch = (dispatch) => ({
  fetchAppSettings: () => dispatch(fetchAppSettings()),
  clientSwitchMaintenanceMode: () => dispatch(clientSwitchMaintenanceMode()),
  checkVersionForMaintenance: () => dispatch(checkVersionForMaintenance()),
  fetchUsers: () => dispatch(fetchUsers()),
  fetchUser: () => dispatch(fetchUser()),
  setIsMobile: (isMobile) => dispatch(setIsMobile(isMobile)),
  setDisplayMode: (displayMode) => dispatch(setDisplayMode(displayMode))
});

export default tabLimitEnforcer({ ErrorComponent: ErrorModal })((withRouter, connect(mapState, mapDispatch))(App));
